import { initializeApp, type FirebaseApp } from 'firebase/app'
import { connectAuthEmulator, getAuth, type Auth } from 'firebase/auth'

import { webLogger } from 'utils/logger'

type FirebaseWebConfig = {
  apiKey: string
  appId: string
  authDomain: string
  projectId: string
}

const normalizeEnvironmentValue = (value: string | undefined): string | undefined => {
  const normalized = value?.trim()
  return normalized && normalized.length > 0 ? normalized : undefined
}

const firebaseConfig: FirebaseWebConfig | null =
  normalizeEnvironmentValue(process.env.REACT_APP_FIREBASE_API_KEY) &&
  normalizeEnvironmentValue(process.env.REACT_APP_FIREBASE_APP_ID) &&
  normalizeEnvironmentValue(process.env.REACT_APP_FIREBASE_AUTH_DOMAIN) &&
  normalizeEnvironmentValue(process.env.REACT_APP_FIREBASE_PROJECT_ID)
    ? {
        apiKey: normalizeEnvironmentValue(process.env.REACT_APP_FIREBASE_API_KEY) as string,
        appId: normalizeEnvironmentValue(process.env.REACT_APP_FIREBASE_APP_ID) as string,
        authDomain: normalizeEnvironmentValue(process.env.REACT_APP_FIREBASE_AUTH_DOMAIN) as string,
        projectId: normalizeEnvironmentValue(process.env.REACT_APP_FIREBASE_PROJECT_ID) as string,
      }
    : null

let firebaseApp: FirebaseApp | undefined
let firebaseAuth: Auth | undefined

if (firebaseConfig) {
  firebaseApp = initializeApp(firebaseConfig)
  firebaseAuth = getAuth(firebaseApp)

  const emulatorHost = normalizeEnvironmentValue(process.env.REACT_APP_FIREBASE_AUTH_EMULATOR_HOST)
  if (emulatorHost) {
    connectAuthEmulator(firebaseAuth, `http://${emulatorHost}`, {
      disableWarnings: true,
    })
  }
} else {
  webLogger.warn('Missing Firebase environment variables', {
    requiredVariables: [
      'REACT_APP_FIREBASE_API_KEY',
      'REACT_APP_FIREBASE_APP_ID',
      'REACT_APP_FIREBASE_AUTH_DOMAIN',
      'REACT_APP_FIREBASE_PROJECT_ID',
    ],
  })
}

export const FirebaseService = {
  auth: firebaseAuth,
  config: firebaseConfig,
  getAuth(): Auth {
    if (!firebaseAuth) {
      throw new Error(
        'Firebase auth is not initialized. Please set REACT_APP_FIREBASE_API_KEY, REACT_APP_FIREBASE_APP_ID, REACT_APP_FIREBASE_AUTH_DOMAIN, and REACT_APP_FIREBASE_PROJECT_ID.'
      )
    }

    return firebaseAuth
  },
}
